/* The one rule this screen exists to honour: **status decides whether `text` is an answer.**
 *
 * `ai-copilot.yaml` states it as a client obligation, because the platform cannot enforce it from
 * its side: every outcome is a 201 carrying prose, and only `answered` is prose a model wrote and a
 * validator accepted. For the other four, `text` is host-written fallback — so a client that renders
 * it as an answer presents the platform's own words as a model's, which is the inverse of the
 * property `core.ai.copilot` is built to hold.
 *
 * Keeping that decision in one pure function rather than in JSX is deliberate: it is testable, and a
 * new `AnswerStatus` member forces a case here rather than silently falling through to "answer".
 */

import type { AnswerStatus } from '@/shared/api/generated/ai-copilot';

/** How the screen must present a response. */
export type Presentation = 'answer' | 'notice' | 'fault';

/**
 * `answer` — model-authored, validated, safe to render as the copilot's reply.
 * `notice` — host-written: nothing retrieved, output refused, or no model reachable.
 * `fault` — host-written and *ours*: a defect in this platform, already logged with a traceback.
 *
 * The three are separated rather than two, because a fault is the one an operator should escalate
 * and an outage is not — the same distinction `UNCHECKABLE` versus `FORGED` draws in the audit seal.
 */
export function presentationFor(status: AnswerStatus): Presentation {
  switch (status) {
    case 'answered':
      return 'answer';
    case 'platform_error':
      return 'fault';
    case 'no_context':
    case 'refused':
    case 'unavailable':
      return 'notice';
  }
}

/** True only when the text may be shown as the copilot's own reply. */
export function isModelAuthored(status: AnswerStatus): boolean {
  return presentationFor(status) === 'answer';
}

/** Tone for the platform's own message, when the status is not an answer. */
export function noticeTone(status: AnswerStatus): 'error' | 'warning' | 'notice' {
  switch (status) {
    /* Ours, and it pages us — the contract says it is logged at ERROR with a traceback. */
    case 'platform_error':
      return 'error';
    /* A model refusal or an outage: real, actionable, not a platform defect. */
    case 'refused':
    case 'unavailable':
      return 'warning';
    case 'no_context':
    case 'answered':
      return 'notice';
  }
}

/** A refused kind, as a sentence rather than a slug. Empty when nothing was refused.
 *
 * Rendered rather than hidden because `refused_kinds` is a deliberate permissions disclosure: an
 * answer thin because of permissions and one thin because nothing matched are different facts, and
 * only this field separates them for the person reading the answer.
 */
export function refusalSummary(refused: readonly string[]): string {
  if (refused.length === 0) return '';
  return refused.join(', ');
}

/* ── What the redesigned screen needs, beyond the three presentations above ──────────────────── */

/** The label a status wears on the screen.
 *
 * **Named for what happened rather than for the enum member**, which is the reference design's
 * choice and the right one: `no_context` is a slug, and "Nothing retrieved" is the sentence a
 * reader can act on. A total map rather than a `switch` with a `default:`, so a sixth status is a
 * compile error in this file instead of a chip that renders with whatever the fallback was — the
 * defect the anomaly screen's `verdictTone` shipped with.
 */
export const STATUS_LABEL = {
  answered: 'Answered',
  no_context: 'Nothing retrieved',
  refused: 'Output refused',
  unavailable: 'No model reachable',
  platform_error: 'Platform fault',
} satisfies Record<AnswerStatus, string>;

/** The colours a status wears: the text/ring hue, the chip fill, and the dot fill.
 *
 * Three values rather than one because the dot is **hollow for `no_context`** — a transparent fill
 * inside a ring, which is how "we retrieved nothing" reads as an absence rather than as a state
 * with its own colour. Collapsing dot into colour would give it a filled mark like the others and
 * make an empty retrieval look like a finding.
 */
export interface StatusTone {
  readonly color: string;
  readonly chip: string;
  readonly dot: string;
}

export const STATUS_TONE = {
  answered: { color: 'var(--status-ok)', chip: 'var(--ok-bg)', dot: 'var(--status-ok)' },
  no_context: {
    color: 'var(--text-secondary)',
    chip: 'var(--un-bg)',
    dot: 'transparent',
  },
  refused: {
    color: 'var(--status-warn)',
    chip: 'var(--warn-bg)',
    dot: 'var(--status-warn)',
  },
  unavailable: {
    color: 'var(--status-warn)',
    chip: 'var(--warn-bg)',
    dot: 'var(--status-warn)',
  },
  platform_error: {
    color: 'var(--status-error)',
    chip: 'var(--err-bg)',
    dot: 'var(--status-error)',
  },
} satisfies Record<AnswerStatus, StatusTone>;

/** One retrieved record, as the receipts rail renders it.
 *
 * `kind` and `ref` are **parsed from the citation string**, which the contract defines as a
 * `[kind:id]` reference. Nothing is invented: a citation that does not carry a kind keeps its whole
 * text as the reference and reports no kind, rather than being given a guessed one.
 */
export interface Receipt {
  readonly n: number;
  readonly kind: string;
  readonly ref: string;
}

/** Splits `[dataset:retail.cards]` into its kind and its reference.
 *
 * Tolerant of the three shapes a real citation takes — bracketed, bare, and neither — because the
 * contract publishes the *convention* rather than a validated format, and a parser that refused an
 * unbracketed one would blank the rail on an answer whose citations are perfectly usable. The kind
 * is only split on the **first** colon: a reference may legitimately contain one, and splitting on
 * the last would turn `dataset:a.b:c` into a kind of `dataset:a.b`.
 */
export function receiptsFrom(citations: readonly string[]): readonly Receipt[] {
  return citations.map((citation, index) => {
    const bare = citation.trim().replace(/^\[/, '').replace(/\]$/, '');
    const colon = bare.indexOf(':');
    return colon === -1
      ? { n: index + 1, kind: '', ref: bare }
      : { n: index + 1, kind: bare.slice(0, colon), ref: bare.slice(colon + 1) };
  });
}

/** The prose, split so a citation token that appears *inside* it becomes a numbered marker.
 *
 * **Derived from the text, never invented.** The reference design authors its prose as segments
 * with a marker after each claim; this platform's API returns one string plus a flat citation list,
 * so there is no segment data to render. Rather than fabricating one — which would put markers
 * against claims nobody tied to a record — this finds the citation tokens the model actually wrote
 * into its answer and numbers those, matching them to the receipt they name.
 *
 * When the text carries no tokens the whole answer is one plain segment and the receipts stay
 * independently selectable. That is the honest degradation: the reader still gets the rail and the
 * claim-to-record link is simply not asserted where the platform cannot assert it.
 */
export interface Segment {
  readonly text: string;
  /** The receipt this marker selects, or `null` for prose with no marker after it. */
  readonly marker: number | null;
}

export function segmentsFrom(
  text: string,
  receipts: readonly Receipt[],
): readonly Segment[] {
  if (receipts.length === 0 || text === '') return [{ text, marker: null }];

  /* **No length ordering, and the closing bracket is why.** A shorter reference cannot be matched
     inside a longer one -- `[dataset:retail.cards]` is not a substring of
     `[dataset:retail.cards_v2]`, because the bracket terminates it -- so the tokens are mutually
     unambiguous and the earliest match is always the right one.

     A longest-first sort was written here first and measured to change nothing: it read as a
     mechanism while being unreachable, which is worse than its absence. What matters instead is
     that both token forms stay *delimited at both ends*; drop the closing bracket for a prefix
     match and `retail.cards` immediately claims `retail.cards_v2`'s marker. */
  const segments: Segment[] = [];
  let rest = text;
  let guard = 0;

  while (rest !== '' && guard < receipts.length * 4) {
    guard += 1;
    let bestAt = -1;
    let bestLen = 0;
    let bestN = 0;
    for (const receipt of receipts) {
      for (const token of [`[${receipt.kind}:${receipt.ref}]`, `[${receipt.ref}]`]) {
        if (token === '[:]' || token === '[]') continue;
        const at = rest.indexOf(token);
        if (at !== -1 && (bestAt === -1 || at < bestAt)) {
          bestAt = at;
          bestLen = token.length;
          bestN = receipt.n;
        }
      }
    }
    if (bestAt === -1) break;
    segments.push({ text: rest.slice(0, bestAt), marker: bestN });
    rest = rest.slice(bestAt + bestLen);
  }

  if (rest !== '') segments.push({ text: rest, marker: null });
  return segments.length === 0 ? [{ text, marker: null }] : segments;
}

/** The sentence under the answer saying who wrote it.
 *
 * **The screen's most important line, and the reason it is a function.** Only `answered` is prose a
 * model wrote and a validator accepted; the other four are host-written fallback. A reader who
 * cannot tell them apart is being shown the platform's own words as a model's, which is the inverse
 * of the property `core.ai.copilot` exists to hold.
 */
export function provenanceOf(
  status: AnswerStatus,
  validator: string | null | undefined,
): string {
  if (!isModelAuthored(status)) {
    return 'Written by Lumiq, not by the model. No model text exists for this question.';
  }
  const named =
    validator === null || validator === undefined || validator === '' ? null : validator;
  return named === null
    ? 'Model-authored and accepted by the validator. Numbered claims point at the receipts beside them.'
    : `Model-authored and accepted by ${named}. Numbered claims point at the receipts beside them.`;
}

/** What the receipts rail says when there is nothing to show.
 *
 * Three sentences rather than one, because the three absences have different causes and a reader
 * acts on each differently: nothing matched, something matched and the output using it was refused,
 * or nothing was answered at all. One message covering all three would tell a reader whose
 * permissions are the problem to go looking at their question.
 */
export function noReceiptsNote(status: AnswerStatus): string {
  switch (status) {
    case 'no_context':
      return 'Nothing was retrieved, which is why no answer was attempted.';
    case 'refused':
      return 'Context was retrieved, but the output that used it was rejected — so there is no claim here to show a receipt for.';
    case 'answered':
    case 'unavailable':
    case 'platform_error':
      return 'No receipts: nothing was answered.';
  }
}
